import {
  Box,
  ListItemButton,
  ListItemContent,
  Stack,
  SvgIcon,
  Typography,
} from "@mui/joy";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import dayjs from "dayjs";
import { useRecoilValue } from "recoil";
import { userAtom } from "recoil/userAtom";
import { MeditationRecordType } from "api/meditationRecordApi";
import { theme } from "styles/theme";
import useOpenMeditationRecord from "hooks/useOpenMeditationRecord";
import useIsRecordSeparatedPage from "../../../hooks/useIsRecordSeparatedPage";
import useUnifiedNavigate from "../../../hooks/useUnifiedNavigate";
import { useNavigate } from "react-router-dom";

export default function MeditationRecordListItem({
  width,
  direction = "column",
  isFirstRecordOfMonth,
  record,
  isSelected,
  setCurrentRecord,
  shouldBeDone,
  durationMinutes,
}: {
  width: string;
  direction?: "row" | "column";
  isFirstRecordOfMonth: boolean;
  record: MeditationRecordType;
  isSelected?: boolean;
  setCurrentRecord: React.Dispatch<
    React.SetStateAction<MeditationRecordType | undefined>
  >;
  shouldBeDone?: boolean;
  durationMinutes?: number;
}) {
  const { meditationRecordKey, date, title, isHighlight, hasAiRecommendation } =
    record;

  const user = useRecoilValue(userAtom);
  const isCoach = user?.role !== "patient";

  const openMeditationRecord = useOpenMeditationRecord(meditationRecordKey);
  const unifiedNavigate = useUnifiedNavigate();
  const navigate = useNavigate();

  const isRecordSeparatedPage = useIsRecordSeparatedPage();
  const hasAiComment = isCoach && hasAiRecommendation;

  return (
    <Stack direction={direction} key={meditationRecordKey}>
      {isFirstRecordOfMonth && (
        <Typography
          variant={"Caption"}
          sx={{
            width: width,
            color: theme.vars.palette.Content.Strong,
            pl: "12px",
            pt: "16px",
            pb: "8px",
            opacity: 0.5,
            fontWeight: 700,
          }}
        >
          {`${dayjs(date).format("MMM")}`}
        </Typography>
      )}
      <ListItemButton
        selected={isRecordSeparatedPage ? false : isSelected}
        sx={{
          width: width,
          p: 0,
          height: "80px",
          alignItems: "center",
          borderRadius: "16px",
          ...(isRecordSeparatedPage && {
            "@media (max-width: 1024px)": {
              "&:hover": {
                // 모바일에서 터치 끝나도 hover로 잡는 문제 때문에 hover은 1024 이상에서만 적용
                // ios : 버튼 클릭 후 touchend 해도 hover
                backgroundColor: "transparent !important",
                boxShadow: "none !important",
                outline: "none !important",
              },
            },
            "&:hover, &.Mui-focusVisible, &.Mui-selected": {
              backgroundColor: "transparent !important",
              boxShadow: "none !important",
              outline: "none !important",
            },
            "&:active": {
              backgroundImage: `linear-gradient(${theme.vars.palette.hoverLight}, ${theme.vars.palette.hoverLight})`,
            },
          }),
        }}
        onClick={() => {
          if (isRecordSeparatedPage) {
            navigate(`/meditation-record-detail?mrk=${meditationRecordKey}`);
            // unifiedNavigate(
            //   `/meditation-record-detail?mrk=${meditationRecordKey}`,
            //   {
            //     type: "card",
            //     gestureEnabled: true,
            //     animation: "default",
            //   }
            // );
          } else {
            openMeditationRecord();
          }
          setCurrentRecord(record);
          (document.activeElement as HTMLElement)?.blur();
        }}
      >
        <ListItemContent>
          <Stack
            key={`meditationRecord_${meditationRecordKey}`}
            direction={"row"}
            alignItems="center"
            sx={{
              position: "relative",
            }}
          >
            {isHighlight && (
              <Box
                sx={{
                  position: "absolute",
                  left: 0,
                  width: "4px",
                  height: "100%",
                  borderRadius: "100px",
                  backgroundColor: theme.vars.palette.Border.Highlight,
                }}
              />
            )}

            <Stack
              direction={"column"}
              justifyContent={"center"}
              alignItems="center"
              sx={{
                ...(direction === "column" && {
                  ml: "8px",
                  mr: "12px",
                }),
                ...(direction === "row" && {
                  pl: "8px",
                }),
                width: "50px",
                minWidth: "50px",
              }}
            >
              <Typography
                variant={"H3"}
                sx={{
                  py: 0,
                  color: theme.vars.palette.Content.Strong,
                  fontWeight: 590,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).get("date")}
              </Typography>
              <Typography
                variant={"Caption"}
                sx={{
                  pb: 0,
                  color: theme.vars.palette.Content.Medium,
                  fontWeight: 400,
                }}
                {...(shouldBeDone && { color: "danger" })}
              >
                {dayjs(date).format("ddd")}
              </Typography>
            </Stack>
            <Typography
              variant={"Body"}
              sx={{
                color: theme.vars.palette.Content.Strong,
                fontWeight: 500,
                flex: 1,
                minWidth: 0,
                pl: 0,
                pr: "20px",
                opacity: direction === "row" ? 0 : 1,
                overflow: "hidden",
                textOverflow: "ellipsis",
                display: "-webkit-box",
                WebkitLineClamp: "2",
                WebkitBoxOrient: "vertical",
              }}
            >
              {title}
              {durationMinutes !== undefined && durationMinutes > 0 && (
                <Typography
                  variant={"Body"}
                  sx={{
                    flex: 1,
                    ml: "2px",
                    color: theme.vars.palette.Content.Weak,
                    fontWeight: 500,
                  }}
                >
                  {`∙ ${durationMinutes}분`}
                </Typography>
              )}
            </Typography>
            {hasAiComment && (
              <SvgIcon
                sx={{
                  width: "16px",
                  height: "16px",
                  mr: "8px",
                  color: "primary.solid",
                }}
              >
                <AutoAwesomeIcon />
              </SvgIcon>
            )}
          </Stack>
        </ListItemContent>
      </ListItemButton>
    </Stack>
  );
}
